<template>
  <Directory
    class="individuals"
    v-bind="{
      activeFilters,
      inputText: filterValues.input_text,
      isLoading,
      results,
      resultCount,
      unitLabels
    }"
    @text-input="filterValues.input_text = $event"
    @clear-all="clearAllFilters"
    @clear-filter="clearFilter"
    @more-click="fetchNextPage"
  >
    <div class="individuals__filters-list" slot="filters">
      <CmSelect
        v-for="filter in filters"
        class="individuals__filter individuals__filter--select"
        :key="filter.id"
        :name="filter.id"
        :options="filter.options"
        :null-label="filter.nullLabel"
        :label="filter.label"
        :multiple="filter.multiple"
        :include-search="filter.options.length > 12"
        :value="filterValues[filter.id]"
        @input="filterValues[filter.id] = $event"
        compact
      />
      <LocationFilter
        class="individuals__filter individuals__filter--select"
        v-model="filterValues.location"
        :location="filterValues.location"
        :radius="filterValues.radius"
        @location="filterValues.location = $event"
        @coords="filterValues.coords = $event"
        @radius="filterValues.radius = $event"
      />
      <Checkbox
        class="individuals__filter individuals__filter--checkbox"
        v-model="filterValues.is_single"
        label="Single"
      />
      <Checkbox
        class="individuals__filter individuals__filter--checkbox"
        v-model="filterValues.is_student"
        label="Student"
      />
      <Checkbox
        class="individuals__filter individuals__filter--checkbox"
        v-model="filterValues.for_hire"
        label="For Hire"
      />
    </div>

    <div class="individuals__list">
      <IndividualCard
        class="individuals__individual"
        v-for="result in results"
        :key="result.id"
        :person="result"
      />
    </div>
  </Directory>
</template>

<script>
// If there's a variable in the template or in the JS that you can't find,
// CHECK THE MIXIN

import IndividualCard from "../company/individual-card";
import DirectoryMixin from "../mixins/directory";

import { preprocessQueryString } from "../lib/elastic-search";
import { getURLParams } from "../lib/url";

export default {
  mixins: [DirectoryMixin],

  components: {
    IndividualCard
  },

  data() {
    let filters = this.generateFilters();

    return {
      filterArray: filters,
      defaultValues: this.generateDefaultFilterValues(filters),
      filterValues: this.generateFilterValues(filters),
      unitLabels: {
        singular: "Individual",
        plural: "Individuals"
      },
      index: "users"
    };
  },

  computed: {
    filterKeys() {
      return [
        "skill_tag_id",
        "is_single",
        "is_student",
        "for_hire",
        "location",
        "coords"
      ];
    },

    activeFilters() {
      const values = this.filterValues;
      const defaults = this.defaultValues;

      return [
        {
          label: "For Hire",
          keys: ["for_hire"],
          value: values.for_hire
        },
        {
          label: "Student",
          keys: ["is_student"],
          value: values.is_student
        },
        {
          label: "Single",
          keys: ["is_single"],
          value: values.is_single
        },
        ...values.skill_tag_id.map(filterValue => ({
          keys: ["skill_tag_id"],
          match: filterValue,
          value: filterValue,
          label: this.filters
            .find(propsFilter => propsFilter.id === "skill_tag_id")
            .options.find(option => option.value === filterValue).label
        })),
        values.coords.lat &&
          values.coords.lng && {
            keys: ["location", "coords"],
            label: `Within ${values.radius} of ${values.location}`
          }
      ]
        .filter(Boolean)
        .filter(filter => filter.value !== defaults[filter.keys[0]]);
    },

    results() {
      if (this.response) {
        return this.response.hits.hits.map(el => {
          return {
            ...el._source
          };
        });
      } else return [];
    },

    query() {
      const filters = this.filterValues;

      return {
        sort: [
          { has_avatar: "desc" },
          filters.sort_key === "recently_updated" && {
            updated_at: "desc"
          },
          filters.sort_key === "newest" && { created_at: "desc" },
          "_score"
        ].filter(Boolean),
        query: {
          bool: {
            must: [
              { match: { status: "active" } },
              { match: { unlisted: false } },
              filters.skill_tag_id.length && {
                terms: {
                  "skill_tags.id": filters.skill_tag_id
                }
              },
              filters.is_single && { match: { is_single: true } },
              filters.is_student && { match: { is_student: true } },
              filters.for_hire && { match: { for_hire: true } },
              filters.coords.lat &&
                filters.coords.lng && {
                  geo_distance: {
                    distance: filters.radius,
                    coordinates: `${filters.coords.lat},${filters.coords.lng}`
                  }
                },
              filters.input_text !== "" && {
                query_string: {
                  query: preprocessQueryString(filters.input_text),
                  default_operator: "AND",
                  fuzziness: 1,
                  fuzzy_prefix_length: 0,
                  fuzzy_max_expansions: 10
                }
              }
            ].filter(Boolean)
          }
        }
      };
    }
  },
  methods: {
    generateFilters() {
      return [
        {
          key: "input_text",
          initial: getURLParams().get("input_text"),
          default: ""
        },
        {
          key: "sort_key",
          initial: getURLParams().get("sort_key"),
          default: "recently_updated"
        },
        {
          key: "skill_tag_id",
          urlKey: "skill_id",
          initial: getURLParams().getAll("skill_id"),
          default: []
        },
        {
          key: "is_single",
          initial: getURLParams().get("is_single"),
          default: false
        },
        {
          key: "is_student",
          initial: getURLParams().get("is_student"),
          default: false
        },
        {
          key: "for_hire",
          initial: getURLParams().get("for_hire"),
          default: false
        },
        {
          key: "radius",
          initial: getURLParams().get("radius"),
          default: "5 miles"
        },
        {
          key: "coords",
          initial: JSON.parse(getURLParams().get("coords")),
          default: { lat: null, lng: null }
        },
        {
          key: "location",
          initial: getURLParams().get("location"),
          default: ""
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
.individuals {
  position: relative;

  &__filters-list {
    display: flex;
    margin: 0 0 0.5rem;
    align-items: baseline;
    @media (max-width: 800px) {
      flex-direction: column;
    }
  }
  &__filter {
    flex: 1 1 auto;
    &--select {
      width: 25%;
      @media (max-width: 800px) {
        width: 100%;
      }
    }
    &--checkbox {
      flex: 0 0 auto;
    }
    & + & {
      margin-left: 0.5rem;
      @media (max-width: 800px) {
        margin-left: 0;
        margin-top: 0.5rem;
      }
    }
  }

  &__list {
    display: grid;
    grid-gap: 2rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding-top: 2rem;
    @include min-width(760px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/javascript/pages/individuals.vue?cfd8e2b8